import type { ApiResponse } from './generic';

/**
 * Billing (docs/product/billing.md). Mirrors the backend `billing` module.
 * Dates are `YYYY-MM-DD` calendar days; money is a number in `currency`.
 */

export enum BillingPlanState {
  Free = 'free',
  Trial = 'trial',
  Paid = 'paid',
  PastDue = 'past_due',
  /** Effective price is 0 (custom price 0 or a 100% discount). */
  Comped = 'comped',
}

export enum BillingInvoiceStatus {
  Open = 'open',
  Paid = 'paid',
  PastDue = 'past_due',
  Void = 'void',
}

export enum BillingInvoiceKind {
  Automatic = 'automatic',
  Manual = 'manual',
}

/** Which period a super admin's "Generate invoice now" covers. */
export enum GenerateInvoicePeriod {
  CurrentToDate = 'current_to_date',
  Previous = 'previous',
}

/** Why "Generate invoice now" produced no invoice. */
export enum InvoiceSkipReason {
  Free = 'free',
  Trial = 'trial',
  ZeroPrice = 'zero_price',
  AlreadyInvoiced = 'already_invoiced',
}

/** `errorCode` of the 402 the paywall answers. */
export enum BillingErrorCode {
  SeatLimitReached = 'BILLING_SEAT_LIMIT_REACHED',
}

/** `errorDetails` of that 402: what the upgrade dialog shows. */
export interface BillingSeatLimitDetails {
  freeLimit: number;
  activeEmployees: number;
  pricePerEmployee: number;
  currency: string;
}

/** `GET companies/:companyId/billing/access` (any company admin). */
export interface BillingAccess {
  /** Billing runs for the company: the Billing page shows. */
  visible: boolean;
  freeLimit: number;
  /** Price per employee after any discount. */
  pricePerEmployee: number;
  currency: string;
}

/** The plan as both portals show it. */
export interface BillingPlanSummary {
  state: BillingPlanState;
  applies: boolean;
  billingEnabled: boolean;
  billingPreview: boolean;
  activeEmployees: number;
  peakThisPeriod: number;
  freeLimit: number;
  currency: string;
  basePrice: number;
  customPrice: number | null;
  discountPercent: number | null;
  discountEndsAt: string | null;
  /** Discount applying today (0 when none or expired). */
  activeDiscountPercent: number;
  /** Price per employee before the discount. */
  listPrice: number;
  /** Price per employee after the discount. */
  effectivePrice: number;
  /** Current period at its peak so far, before tax. */
  estimatedAmount: number;
  trialEndsAt: string | null;
  trialActive: boolean;
  billingAnchorDate: string | null;
  periodStart: string;
  periodEnd: string;
  /** Next automatic invoice; null before the company is billable. */
  nextInvoiceDate: string | null;
  hasPaymentMethod: boolean;
  pastDue: boolean;
}

/** Card metadata only: never the number or security code. */
export interface BillingPaymentMethod {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export interface BillingContact {
  billingName: string | null;
  billingEmail: string | null;
  billingPhone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string | null;
  accountingEmail: string | null;
  autoEmailInvoices: boolean;
}

export type UpdateBillingContactPayload = Partial<BillingContact>;

/** `GET companies/:companyId/billing` (billing.read). */
export interface CompanyBillingOverview {
  plan: BillingPlanSummary;
  paymentMethod: BillingPaymentMethod | null;
  /** Stripe is configured: "Add payment method" works. */
  paymentsEnabled: boolean;
  contact: BillingContact;
}

export type BillingTaxCode = 'GST' | 'HST' | 'QST' | 'PST' | 'RST';

export interface BillingTaxLine {
  code: BillingTaxCode;
  /** Percent, e.g. 9.975. */
  rate: number;
  amount: number;
  province: string | null;
  registrationNumber: string;
}

export interface BillingInvoice {
  id: string;
  /** KHR-000001 */
  number: string;
  kind: BillingInvoiceKind;
  status: BillingInvoiceStatus;
  periodStart: string;
  periodEnd: string;
  /** Peak active employees in the period. */
  quantity: number;
  freeLimit: number;
  unitPrice: number;
  discountPercent: number;
  subtotal: number;
  discountAmount: number;
  netAmount: number;
  taxes: BillingTaxLine[];
  taxTotal: number;
  total: number;
  currency: string;
  issuedAt: string;
  dueAt: string;
  paidAt: string | null;
  emailedAt: string | null;
  emailedTo: string | null;
  paymentError: string | null;
}

export interface BillingInvoicePage {
  items: BillingInvoice[];
  total: number;
  page: number;
  limit: number;
}

export interface BillingInvoiceListParams {
  page?: number;
  limit?: number;
}

/** Admin: one change in a company's plan history. */
export interface CompanySubscriptionEvent {
  id: string;
  actor: { id: string; name: string } | null;
  changes: Record<string, { from: unknown; to: unknown }>;
  createdAt: string;
}

export interface CompanySubscriptionOverrides {
  trialEndsAt: string | null;
  customPricePerEmployee: number | null;
  discountPercent: number | null;
  discountEndsAt: string | null;
  internalNote: string | null;
  billingPreview: boolean;
  billingAnchorDate: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; name: string } | null;
}

/** Admin: `GET admin/companies/:companyId/subscription`. */
export interface CompanySubscriptionView {
  plan: BillingPlanSummary;
  overrides: CompanySubscriptionOverrides;
  events: CompanySubscriptionEvent[];
  paymentsEnabled: boolean;
}

/** Admin: PATCH body; omit a field to keep it, null clears it. */
export interface UpdateCompanySubscriptionPayload {
  trialEndsAt?: string | null;
  customPricePerEmployee?: number | null;
  discountPercent?: number | null;
  discountEndsAt?: string | null;
  internalNote?: string | null;
  billingPreview?: boolean;
  billingAnchorDate?: string | null;
}

export interface GenerateInvoiceResult {
  invoice: BillingInvoice | null;
  skipped: InvoiceSkipReason | null;
}

export type BillingPriceCurrency = 'CAD' | 'USD';

export interface SellerTaxNumbers {
  gstHst: string | null;
  qst: string | null;
  bcPst: string | null;
  skPst: string | null;
  mbRst: string | null;
}

export interface BillingSeller {
  name: string;
  addressLines: string[];
  email: string | null;
  taxNumbers: SellerTaxNumbers;
}

/** Admin: `GET/PUT admin/billing/settings`. */
export interface BillingSettings {
  enabled: boolean;
  freeEmployeeLimit: number;
  pricePerEmployee: { amount: number; currency: BillingPriceCurrency };
  seller: BillingSeller;
}

export type UpdateBillingSettingsPayload = Partial<BillingSettings>;

export type BillingAccessResponse = ApiResponse<BillingAccess>;
export type CompanyBillingOverviewResponse =
  ApiResponse<CompanyBillingOverview>;
export type BillingContactResponse = ApiResponse<BillingContact>;
export type BillingInvoicePageResponse = ApiResponse<BillingInvoicePage>;
export type BillingInvoiceResponse = ApiResponse<BillingInvoice>;
export type BillingCheckoutResponse = ApiResponse<{ url: string }>;
export type CompanySubscriptionResponse = ApiResponse<CompanySubscriptionView>;
export type GenerateInvoiceResponse = ApiResponse<GenerateInvoiceResult>;
export type BillingSettingsResponse = ApiResponse<BillingSettings>;
